import React, { FC, ReactNode } from 'react';
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface SegmentedOption {
  value: string;
  /** Accessible label; also the hover tooltip when `iconOnly` is set. */
  label: string;
  icon: ReactNode;
}

interface SegmentedToggleProps {
  value: string;
  options: SegmentedOption[];
  onChange: (value: string) => void;
  /** Icon-only segments (label surfaces as a tooltip). Defaults to icon + text. */
  iconOnly?: boolean;
  size?: 'small' | 'medium';
  ariaLabel?: string;
}

/**
 * A pill "track" segmented control. The selected segment gets a solid brand-red
 * fill with a white icon/label; unselected segments stay muted and transparent,
 * so the active choice is unmistakable. Shared by the feed view toggle and the
 * post media-layout selector to keep selection styling consistent.
 */
const SegmentedToggle: FC<SegmentedToggleProps> = ({
  value,
  options,
  onChange,
  iconOnly = false,
  size = 'small',
  ariaLabel,
}) => {
  const theme = useTheme();
  const compact = size === 'small';

  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(_event, next) => {
        // Exclusive groups emit null when re-clicking the active segment; keep it selected.
        if (next !== null) onChange(next);
      }}
      aria-label={ariaLabel}
      sx={{
        p: '3px',
        gap: '3px',
        borderRadius: 'var(--radius-pill)',
        backgroundColor: theme.tokens.colors.backgroundCardHover,
        border: `${theme.tokens.borders.widthThin} ${theme.tokens.borders.style} ${theme.tokens.colors.cardBorder}`,
        '& .MuiToggleButtonGroup-grouped': {
          m: 0,
          border: 0,
          borderRadius: 'var(--radius-pill) !important',
          textTransform: 'none',
          gap: 0.75,
          minWidth: iconOnly ? (compact ? 40 : 44) : 'auto',
          height: compact ? 34 : 38,
          px: iconOnly ? 0 : compact ? 1.5 : 2,
          fontFamily: theme.tokens.typography.monoFontFamily,
          fontWeight: theme.tokens.typography.fontWeightMedium,
          fontSize: compact ? 12 : 13,
          color: theme.tokens.colors.text3,
          transition: `background-color ${theme.tokens.animations.fast} ${theme.tokens.animations.easing}, color ${theme.tokens.animations.fast} ${theme.tokens.animations.easing}`,
          '& .MuiSvgIcon-root': { fontSize: compact ? 18 : 20 },
          '&:hover': {
            backgroundColor: theme.tokens.colors.glassLine,
            color: theme.tokens.colors.textPrimary,
          },
          '&.Mui-selected': {
            backgroundColor: theme.tokens.colors.primary,
            color: '#fff',
            '&:hover': { backgroundColor: theme.tokens.colors.primaryDark },
          },
        },
      }}
    >
      {options.map(option => {
        const button = (
          <ToggleButton key={option.value} value={option.value} aria-label={option.label}>
            {option.icon}
            {!iconOnly && option.label}
          </ToggleButton>
        );
        return iconOnly ? (
          <Tooltip key={option.value} title={option.label}>
            {button}
          </Tooltip>
        ) : (
          button
        );
      })}
    </ToggleButtonGroup>
  );
};

export default SegmentedToggle;
